import type { UserListQuery } from '@lib/input/user-list.input'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { userService } from '@/services/UserService'
import { userKeys, venueKeys } from '@/hooks/queryKeys'
import type { AssignableRolesDto, ManagedVenuesDto, UserDto, UserListDto } from '@lib/dto/user.dto'
import type { InviteUserInput, SetManagedVenuesInput, UpdateUserInput } from '@lib/input/user.input'

/** The company's people (People View, company-wide; scoped by the API). `enabled` false asks for nothing. */
export function useUsers(query: UserListQuery = {}, page?: number, enabled = true): UseQueryResult<UserListDto> {
  return useQuery({
    queryKey: userKeys.list(query, page),
    queryFn: () => userService.list(query, page),
    enabled,
    staleTime: 30_000,
    // The previous page's rows stay on screen while the next one loads — see
    // the note on `useVenues`.
    placeholderData: (previous) => previous,
  })
}

/** The company's roles, and which the session may give (People View and up). */
export function useAssignableRoles(enabled = true): UseQueryResult<AssignableRolesDto> {
  return useQuery({
    queryKey: userKeys.assignableRoles(),
    queryFn: () => userService.assignableRoles(),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * After a change to someone, or to a role people hold: everything read about
 * people (the lists, each person's venues, the roles the picker offers), and
 * every venue's staff, which show the same people from where they work.
 */
export function refreshPeople(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: userKeys.all })
  void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === venueKeys.all[0] && query.queryKey.includes('managers') })
}

/** Invite a user; refreshes the lists on success. */
export function useInviteUser(): UseMutationResult<UserDto, Error, InviteUserInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteUserInput) => userService.invite(input),
    onSuccess: () => refreshPeople(queryClient),
  })
}

/** Edit a user's name/role; refreshes the list on success. */
export function useUpdateUser(): UseMutationResult<UserDto, Error, { id: string; input: UpdateUserInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateUserInput }) => userService.update(vars.id, vars.input),
    onSuccess: () => refreshPeople(queryClient),
  })
}

/**
 * Withdraw an invitation nobody accepted, removing the user. Only offered for a
 * PENDING user: deactivating one leaves a row for someone who never arrived, and
 * keeps hold of their email address so a mistyped invite can never be corrected.
 */
export function useDeleteInvitedUser(): UseMutationResult<void, Error, { id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string }) => userService.deletePending(vars.id),
    onSuccess: () => refreshPeople(queryClient),
  })
}

/** Deactivate a user (blocks sign-in); refreshes the list on success. */
export function useDeactivateUser(): UseMutationResult<UserDto, Error, { id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string }) => userService.deactivate(vars.id),
    onSuccess: () => refreshPeople(queryClient),
  })
}

/** Reactivate a deactivated user; refreshes the list on success. */
export function useReactivateUser(): UseMutationResult<UserDto, Error, { id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string }) => userService.reactivate(vars.id),
    onSuccess: () => refreshPeople(queryClient),
  })
}

/** Re-send a pending user's invitation email. */
export function useResendInvite(): UseMutationResult<void, Error, { id: string }> {
  return useMutation({
    mutationFn: (vars: { id: string }) => userService.resendInvite(vars.id),
  })
}

/** Venues a venue-admin user is assigned to manage. */
export function useManagedVenues(userId: string, enabled = true): UseQueryResult<ManagedVenuesDto> {
  return useQuery({
    queryKey: userKeys.managedVenues(userId),
    queryFn: () => userService.listManagedVenues(userId),
    enabled: enabled && userId !== '',
    staleTime: 15_000,
  })
}

/** Replace the venues a venue admin manages; refreshes managers caches. */
export function useSetManagedVenues(): UseMutationResult<
  ManagedVenuesDto,
  Error,
  { userId: string; input: SetManagedVenuesInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { userId: string; input: SetManagedVenuesInput }) =>
      userService.setManagedVenues(vars.userId, vars.input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.managedVenues(vars.userId) })
      // Venue settings "Managers" lists depend on the same links.
      void queryClient.invalidateQueries({ queryKey: venueKeys.all })
    },
  })
}
